"use client"

import type React from "react"
import { useState } from "react"
import {
  Search,
  Settings,
  User,
  Power,
  FileText,
  Folder,
  Chrome,
  Mail,
  Calendar,
  Music,
  Image,
  Video,
  Terminal,
} from "lucide-react"

interface StartMenuProps {
  onAppClick: (id: string, title: string, component: React.ReactNode) => void
}

const StartMenu = ({ onAppClick }: StartMenuProps) => {
  const [searchQuery, setSearchQuery] = useState("")

  const apps = [
    { id: "explorer", name: "File Explorer", icon: <Folder className="w-6 h-6 text-yellow-500" /> },
    { id: "chrome", name: "Chrome", icon: <Chrome className="w-6 h-6 text-blue-500" /> },
    { id: "mail", name: "Mail", icon: <Mail className="w-6 h-6 text-blue-400" /> },
    { id: "calendar", name: "Calendar", icon: <Calendar className="w-6 h-6 text-blue-600" /> },
    { id: "photos", name: "Photos", icon: <Image className="w-6 h-6 text-teal-500" /> },
    { id: "music", name: "Music", icon: <Music className="w-6 h-6 text-pink-500" /> },
    { id: "videos", name: "Videos", icon: <Video className="w-6 h-6 text-purple-500" /> },
    { id: "settings", name: "Settings", icon: <Settings className="w-6 h-6 text-gray-500" /> },
    { id: "terminal", name: "Terminal", icon: <Terminal className="w-6 h-6 text-white" /> },
    { id: "notepad", name: "Notepad", icon: <FileText className="w-6 h-6 text-blue-400" /> },
  ]

  const filteredApps = searchQuery
    ? apps.filter((app) => app.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : apps

  const handleAppClick = (id: string) => {
    const app = apps.find((a) => a.id === id)
    if (!app) return

    let component

    if (id === "explorer") {
      component = <div className="w-full h-full bg-white">File Explorer Content</div>
    } else if (id === "settings") {
      component = (
        <div className="w-full h-full bg-gray-100 p-4">
          <div className="grid grid-cols-3 gap-4">
            {[
              "System",
              "Bluetooth",
              "Network",
              "Personalization",
              "Apps",
              "Accounts",
              "Time & Language",
              "Gaming",
              "Accessibility",
            ].map((item) => (
              <div
                key={item}
                className="bg-white p-4 rounded-lg shadow hover:bg-gray-50 cursor-pointer flex items-center"
              >
                <Settings className="w-6 h-6 mr-2 text-blue-500" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      )
    } else {
      component = <div className="w-full h-full bg-white flex items-center justify-center">{app.name} Content</div>
    }

    onAppClick(id, app.name, component)
  }

  return (
    <div
      id="start-menu"
      className="absolute bottom-12 left-0 w-96 bg-black bg-opacity-80 backdrop-blur-md rounded-lg shadow-lg overflow-hidden z-50"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Search */}
      <div className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Type here to search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-gray-700 text-white placeholder-gray-400 rounded-md py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Pinned Apps */}
      <div className="px-4 pb-2">
        <h3 className="text-white text-sm font-medium mb-2">Pinned</h3>
        <div className="grid grid-cols-4 gap-4">
          {filteredApps.slice(0, 8).map((app) => (
            <button
              key={app.id}
              className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-white hover:bg-opacity-10 transition-colors"
              onClick={() => handleAppClick(app.id)}
            >
              <div className="w-10 h-10 bg-gray-700 rounded-md flex items-center justify-center mb-1">{app.icon}</div>
              <span className="text-white text-xs text-center">{app.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Recommended */}
      <div className="px-4 pb-4">
        <h3 className="text-white text-sm font-medium mb-2">Recommended</h3>
        <div className="grid grid-cols-2 gap-2">
          {["Recently edited", "Downloads", "Pictures", "Most used"].map((item) => (
            <button
              key={item}
              className="flex items-center p-2 rounded-lg hover:bg-white hover:bg-opacity-10 transition-colors"
            >
              <FileText className="w-5 h-5 text-gray-400 mr-3" />
              <span className="text-white text-sm">{item}</span>
            </button>
          ))}
        </div>
      </div>

      {/* User and Power */}
      <div className="border-t border-gray-700 p-4 flex items-center justify-between">
        <button className="flex items-center hover:bg-white hover:bg-opacity-10 p-2 rounded-lg transition-colors">
          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center mr-2">
            <User className="w-5 h-5 text-white" />
          </div>
          <span className="text-white text-sm">User</span>
        </button>
        <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white hover:bg-opacity-10 transition-colors">
          <Power className="w-5 h-5 text-white" />
        </button>
      </div>
    </div>
  )
}

export default StartMenu

